/**
 * Workflow B — Everyday Guide (docs/03 §10).
 * Same engine (`rankCards`), batched over fixed categories × amount-band midpoints.
 * Pure: no I/O. Caller caches and recomputes when portfolio inputs change.
 */

import { asCents, type Cents } from '../money';
import type { BusinessDate } from '../time';
import { rankCards } from './rankCards';
import type { PriorityMode, RankCardsInput, SpendCategory } from './types';

/** Exact secondary-reference label from docs/03 §10. */
export const EVERYDAY_GUIDE_SUBTITLE =
  'Best card for a typical $100 purchase, assuming current capacity and eligibility.';

/** Shown when `rankCards` returns no eligible card for that band. */
export const NO_ELIGIBLE_CARD_MARK = '—';

/**
 * Workflow B category ids (user-resolved Task 8 list) → `spend_category` the engine scores.
 * Labels that are not enum members map to the matching scored category:
 * dining→restaurants, travel→travel_portal, general→other.
 */
export type WorkflowBCategoryId =
  'groceries' | 'dining' | 'gas' | 'travel' | 'streaming' | 'general';

export type WorkflowBCategory = {
  id: WorkflowBCategoryId;
  /** Display / row label (Workflow B name). */
  label: string;
  /** Category passed to `rankCards` as a user override (Stage 0 authority #1). */
  spend_category: SpendCategory;
};

export const WORKFLOW_B_CATEGORIES: readonly WorkflowBCategory[] = [
  { id: 'groceries', label: 'groceries', spend_category: 'groceries' },
  { id: 'dining', label: 'dining', spend_category: 'restaurants' },
  { id: 'gas', label: 'gas', spend_category: 'gas' },
  { id: 'travel', label: 'travel', spend_category: 'travel_portal' },
  { id: 'streaming', label: 'streaming', spend_category: 'streaming' },
  { id: 'general', label: 'general', spend_category: 'other' },
] as const;

export type AmountBandId = 'band_0_100' | 'band_101_300' | 'band_301_500' | 'band_500_plus';

export type AmountBand = {
  id: AmountBandId;
  /** Display label from docs/03 §10. */
  label: string;
  /** Midpoint query amount (Task 8 resolution). */
  amount_cents: Cents;
};

export const AMOUNT_BANDS: readonly AmountBand[] = [
  { id: 'band_0_100', label: '$0–100', amount_cents: asCents(5_000) },
  { id: 'band_101_300', label: '$101–300', amount_cents: asCents(20_000) },
  { id: 'band_301_500', label: '$301–500', amount_cents: asCents(40_000) },
  { id: 'band_500_plus', label: '$500+', amount_cents: asCents(75_000) },
] as const;

export type EverydayGuideBandCell = {
  band_id: AmountBandId;
  band_label: string;
  amount_cents: Cents;
  winner_card_id: string | null;
  /** Card display label, or `—` when none eligible. */
  winner_label: string;
};

export type EverydayGuideCategoryRow = {
  id: WorkflowBCategoryId;
  label: string;
  spend_category: SpendCategory;
  bands: readonly EverydayGuideBandCell[];
};

export type EverydayGuide = {
  subtitle: typeof EVERYDAY_GUIDE_SUBTITLE;
  computed_at: BusinessDate;
  mode: PriorityMode;
  rows: readonly EverydayGuideCategoryRow[];
};

/** Portfolio snapshot shared across every guide query (everything except query + merchant). */
export type EverydayGuideSnapshot = Omit<RankCardsInput, 'query' | 'merchant'>;

export type BuildEverydayGuideInput = {
  snapshot: EverydayGuideSnapshot;
  /** Map of card_id → display label (e.g. "Test Issuer A ···1111"). */
  cardLabels: Readonly<Record<string, string>>;
  computedAt: BusinessDate;
  /** Defaults to maximize_rewards (docs/03 §11 / user default priority mode). */
  mode?: PriorityMode;
};

function cellFor(
  band: AmountBand,
  winnerCardId: string | null,
  cardLabels: Readonly<Record<string, string>>,
): EverydayGuideBandCell {
  if (winnerCardId === null) {
    return {
      band_id: band.id,
      band_label: band.label,
      amount_cents: band.amount_cents,
      winner_card_id: null,
      winner_label: NO_ELIGIBLE_CARD_MARK,
    };
  }
  const label = cardLabels[winnerCardId];
  return {
    band_id: band.id,
    band_label: band.label,
    amount_cents: band.amount_cents,
    winner_card_id: winnerCardId,
    winner_label: label ?? winnerCardId,
  };
}

/**
 * Run `rankCards` once per (Workflow B category × amount band).
 * Identical inputs → identical output.
 */
export function buildEverydayGuide(input: BuildEverydayGuideInput): EverydayGuide {
  const mode = input.mode ?? 'maximize_rewards';
  const rows: EverydayGuideCategoryRow[] = [];

  for (const category of WORKFLOW_B_CATEGORIES) {
    const bands: EverydayGuideBandCell[] = [];
    for (const band of AMOUNT_BANDS) {
      const out = rankCards({
        ...input.snapshot,
        asOf: input.snapshot.asOf,
        merchant: null,
        query: {
          merchant_input: `Everyday guide · ${category.label}`,
          merchant_id: null,
          category: category.spend_category,
          amount_cents: band.amount_cents,
          channel: 'any',
          geography: 'domestic',
          recurring: false,
          owner: 'personal',
          financing_requested: false,
          return_risk: false,
          mode,
          pay_before_statement_close: null,
          allow_interest_bearing: false,
        },
      });
      bands.push(cellFor(band, out.winner_card_id, input.cardLabels));
    }
    rows.push({
      id: category.id,
      label: category.label,
      spend_category: category.spend_category,
      bands,
    });
  }

  return {
    subtitle: EVERYDAY_GUIDE_SUBTITLE,
    computed_at: input.computedAt,
    mode,
    rows,
  };
}
